/**
 * Chat Panel
 *
 * Bottom-left overlay panel for the AI script assistant.
 * Opened/closed via the header sparkle button (or Cmd+K).
 * Features a drag-to-resize handle, header bar with controls,
 * scrollable message area, and prompt input bar.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { resolveObjectRef } from 'trace-viewer/editor';
import { agentService, type CredentialGap } from './AgentService';
import { toChatMessages } from './session-file';
import { ChatMessageComponent, getToolDetail, getToolObjectRef, type MessageData } from './ChatMessage';
import { ApiKeySettings, openCatalogPanel } from './ApiKeySettings';
import { CreditsChip } from './CreditsDisplay';
import { SCENE_FILE_ACCEPT, splitSceneFiles } from './scene-files';
import { describeGatewayError, ensureGatewayDiscovery, isGatewayAvailable, onGatewayModelsChange, uploadGatewayFile, type GatewayDocument } from './gateway';
import { getToolDefinition } from './tools/tool-registry';
import { refreshCredits } from './credits';
import type { ToolContext } from './agent-helpers';
import { onCatalogTokenChange } from '../catalog-auth';
import * as styles from './chat-styles';
import { parseSlashInput, matchSlashCommands, executeSlashCommand, getSlashCommands, type SlashCommand } from './slash-commands';
import { getKitManifest } from './kits';
import { isToolErrorResult } from './tool-errors';
import { useVoiceInput } from './useVoiceInput';
import { getAutoplayEnabled, setAutoplayEnabled, speakMessage, cancelTts } from './tts-playback';
import type { AssistantPins, MediaToggles } from './agent-types';
import { TEMPLATE_LIBRARY } from './templates/library';
import { listStyles } from './styles/library';
import { DESIGN_SETS } from './templates/design-sets';
import { WRAPPER_LIBRARY } from './templates/wrappers';

interface ChatPanelProps {
    visible: boolean;
    onClose: () => void;
}

const MIN_PANEL_HEIGHT = 200;
const MAX_PANEL_HEIGHT_RATIO = 0.85;
const DEFAULT_PANEL_HEIGHT = 400;
const DEFAULT_PANEL_WIDTH = styles.PANEL_WIDTH;

/** Sparkle icon SVG — matches the header toggle button */
const SparkleIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 0C8 0 8.9 3.2 9.8 4.8C10.7 6.4 14 7 14 7C14 7 10.7 7.6 9.8 9.2C8.9 10.8 8 14 8 14C8 14 7.1 10.8 6.2 9.2C5.3 7.6 2 7 2 7C2 7 5.3 6.4 6.2 4.8C7.1 3.2 8 0 8 0Z"/>
        <circle cx="13.5" cy="2.5" r="1.2"/>
        <circle cx="3" cy="12.5" r="0.9"/>
    </svg>
);

/** Pulsing thinking indicator shown during processing */
const ThinkingIndicator: React.FC = () => {
    const [dots, setDots] = React.useState(0);
    React.useEffect(() => {
        const id = setInterval(() => setDots(d => (d + 1) % 4), 400);
        return () => clearInterval(id);
    }, []);
    return (
        <div style={{
            alignSelf: 'flex-start',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '6px 12px',
            background: styles.colors.bgLight,
            borderRadius: '12px 12px 12px 2px',
            fontSize: 13,
            color: styles.colors.accent,
            flexShrink: 0,
        }}>
            <span style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                flexShrink: 0,
                marginTop: 5,
                borderRadius: '50%',
                background: styles.colors.accent,
                animation: 'ai-pulse 1.2s ease-in-out infinite',
            }} />
            <span style={{ color: styles.colors.textMuted }}>
                Thinking{'.'.repeat(dots)}
            </span>
        </div>
    );
};

// ── Slash Command Suggestions ───────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
    generate: 'Generate',
    inference: 'Inference',
    create: 'Create',
    scene: 'Scene',
    script: 'Script',
};

const SlashSuggestions: React.FC<{
    filter: string;
    selectedIndex: number;
    onSelect: (cmd: SlashCommand) => void;
}> = ({ filter, selectedIndex, onSelect }) => {
    const commands = useMemo(() => {
        if (filter === '') return getSlashCommands() as SlashCommand[];
        return matchSlashCommands(filter);
    }, [filter]);

    if (commands.length === 0) return null;

    // Group by category
    const grouped = new Map<string, SlashCommand[]>();
    for (const cmd of commands) {
        const list = grouped.get(cmd.category) ?? [];
        list.push(cmd);
        grouped.set(cmd.category, list);
    }

    let flatIndex = 0;

    return (
        <div style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            maxHeight: 260,
            overflowY: 'auto',
            background: styles.colors.bgLight,
            border: `1px solid ${styles.colors.border}`,
            borderBottom: 'none',
            borderRadius: '8px 8px 0 0',
            zIndex: 10,
        }}>
            {[...grouped.entries()].map(([category, cmds]) => (
                <div key={category}>
                    <div style={{
                        padding: '4px 12px 2px',
                        fontSize: 10,
                        fontWeight: 600,
                        color: styles.colors.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        userSelect: 'none',
                    }}>{CATEGORY_LABELS[category] ?? category}</div>
                    {cmds.map(cmd => {
                        const isSelected = flatIndex === selectedIndex;
                        const idx = flatIndex++;
                        return (
                            <div
                                key={cmd.name}
                                onMouseDown={(e) => { e.preventDefault(); onSelect(cmd); }}
                                data-index={idx}
                                style={{
                                    padding: '5px 12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: 8,
                                    background: isSelected ? styles.colors.accent + '33' : 'transparent',
                                    ...(isSelected ? { borderLeft: `2px solid ${styles.colors.accent}` } : {}),
                                }}
                            >
                                <span style={{
                                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                    fontSize: 12,
                                    color: styles.colors.accent,
                                    fontWeight: 600,
                                    flexShrink: 0,
                                }}>/{cmd.name}</span>
                                <span style={{
                                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                    fontSize: 11,
                                    color: styles.colors.textMuted,
                                    opacity: 0.6,
                                    flexShrink: 0,
                                }}>{cmd.usage}</span>
                                <span style={{
                                    fontSize: 11,
                                    color: styles.colors.textMuted,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>{cmd.description}</span>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

/** What a blocked creator is told, and where the button takes them. */
function describeCredentialGap(gap: CredentialGap): {
    message: string;
    action: string;
    placeholder: string;
} {
    switch (gap) {
        case 'signed-out':
            return {
                message: 'Sign in to Trace to use the assistant',
                action: 'Sign in',
                placeholder: 'Sign in to Trace to get started...',
            };
        case 'unconfigured':
            return {
                message: 'This build has no AI gateway, so the assistant cannot run',
                action: 'Open settings',
                placeholder: 'The assistant is unavailable in this build...',
            };
    }
}

// ── Generation Controls ─────────────────────────────────────

const PIN_FIELDS: { kind: keyof AssistantPins; label: string; options: { value: string; label: string }[] }[] = [
    { kind: 'template', label: 'Template', options: TEMPLATE_LIBRARY.map(t => ({ value: t.slug, label: t.name })) },
    { kind: 'style', label: 'Style', options: listStyles().map(s => ({ value: s.slug, label: s.label })) },
    { kind: 'set', label: 'Set', options: DESIGN_SETS.map(set => ({ value: set, label: set })) },
    { kind: 'wrapper', label: 'Wrapper', options: WRAPPER_LIBRARY.map(w => ({ value: w.id, label: w.label })) },
];

// No video toggle: the assistant has no video tool.
const MEDIA_FIELDS: { kind: keyof MediaToggles; label: string }[] = [
    { kind: 'images', label: 'Images' },
    { kind: 'models3d', label: '3D' },
    { kind: 'materials', label: 'Materials' },
];

/**
 * The web Home page's generation controls, above the input: pin a template,
 * style, set or wrapper instead of letting the model pick, and switch generation types
 * off. Stored on AgentService, so the choices survive a reload.
 */
const GenerationControls: React.FC = () => {
    const [pins, setPins] = useState(() => agentService.getAssistantPins());
    const [toggles, setToggles] = useState(() => agentService.getMediaToggles());
    useEffect(() => agentService.onGenerationControlsChange(() => {
        setPins(agentService.getAssistantPins());
        setToggles(agentService.getMediaToggles());
    }), []);

    return (
        <div style={styles.pinBar}>
            {PIN_FIELDS.map(({ kind, label, options }) => (
                <label key={kind} style={styles.pinField}>
                    {label}
                    <select
                        aria-label={`Pin ${label.toLowerCase()}`}
                        value={pins[kind] ?? ''}
                        onChange={(e) => agentService.setAssistantPin(kind, e.target.value || null)}
                        style={styles.pinSelect}
                    >
                        <option value="">Auto</option>
                        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </label>
            ))}
            {MEDIA_FIELDS.map(({ kind, label }) => (
                <label key={kind} style={styles.pinToggle}>
                    <input
                        type="checkbox"
                        checked={toggles[kind]}
                        onChange={(e) => agentService.setMediaToggle(kind, e.target.checked)}
                    />
                    {label}
                </label>
            ))}
        </div>
    );
};

let messageIdCounter = Date.now();
/** Uploads the gateway accepts (its MAX_BODY_BYTES default). */
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
/** What the file picker offers: what a model can read, and files to add to the scene. */
const ATTACHMENT_ACCEPT = `application/pdf,image/png,image/jpeg,image/webp,image/gif,text/plain,text/markdown,text/csv,.md,.txt,.csv,${SCENE_FILE_ACCEPT}`;

/**
 * A file in the prompt bar: uploaded as soon as it is added, sent with the
 * next message. Scene files (models, video, audio) are instead added to the
 * scene as soon as they are added, and the next message tells the model.
 */
interface PendingAttachment {
    id: string;
    name: string;
    status: 'uploading' | 'ready' | 'error';
    doc?: GatewayDocument;
    error?: string;
    /** Kept for a retry after an upload error. */
    file?: File;
    inScene?: boolean;
}

function nextId(): string {
    return `msg-${++messageIdCounter}`;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ visible, onClose }) => {
    const [messages, setMessages] = useState<MessageData[]>([]);
    // A conversation picked up from a scene file replaces what the panel shows.
    useEffect(() => agentService.onSessionRestored((file) => {
        setMessages(toChatMessages(file).map(message => ({ ...message, id: nextId() })));
    }), []);
    const [inputText, setInputText] = useState('');
    const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
    const [dropActive, setDropActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [panelHeight, setPanelHeight] = useState(DEFAULT_PANEL_HEIGHT);
    const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
    const [modelString, setModelString] = useState(agentService.getModelString());
    const [slashFilter, setSlashFilter] = useState<string | null>(null);
    const [slashSelectedIndex, setSlashSelectedIndex] = useState(0);
    const [autoplay, setAutoplay] = useState(getAutoplayEnabled);

    // Signing in, or the routing table arriving, changes which model answers —
    // and in Trace AI mode changes the header from a model id to "Trace AI".
    // The tick is what clears the sign-in prompt: in Trace AI mode the label
    // reads "Trace AI" before and after login, so setModelString alone is a
    // no-op re-render and the panel would keep asking a signed-in user to
    // sign in until something else happened to redraw it.
    const [, setAuthTick] = useState(0);
    useEffect(() => {
        const refresh = (): void => {
            setModelString(agentService.getModelString());
            setAuthTick(n => n + 1);
        };
        const offToken = onCatalogTokenChange(refresh);
        const offRoutes = onGatewayModelsChange(refresh);
        return () => { offToken(); offRoutes(); };
    }, []);

    // Recomputed every render so closing settings (a pasted key, a switched
    // source) reflects immediately.
    const credentialGap = agentService.credentialGap();
    const gapCopy = credentialGap ? describeCredentialGap(credentialGap) : null;

    /**
     * Take the user to the fix for the current gap: sign-in lives in the
     * catalog panel, every other remedy in AI settings. Re-read on click
     * rather than closed over, so a gap that changed since this render (a
     * sign-in landing in another tab) still routes to the right place.
     */
    const resolveCredentialGap = useCallback(() => {
        if (agentService.credentialGap() === 'signed-out') openCatalogPanel();
        else setShowSettings(true);
    }, []);

    const toggleAutoplay = useCallback(() => {
        setAutoplay(prev => {
            const next = !prev;
            setAutoplayEnabled(next);
            if (!next) cancelTts();
            return next;
        });
    }, []);

    const voice = useVoiceInput();

    const handleMicClick = useCallback(async () => {
        if (voice.state === 'recording') {
            const transcript = await voice.finishRecording();
            if (transcript) {
                setInputText(prev => prev ? prev + ' ' + transcript : transcript);
                // Focus the input so user can review/edit before sending
                setTimeout(() => inputRef.current?.focus(), 50);
            }
        } else if (voice.state === 'idle') {
            void voice.startRecording();
        }
    }, [voice]);

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);
    const dragStartY = useRef(0);
    const dragStartHeight = useRef(0);
    const isDraggingWidth = useRef(false);
    const dragStartX = useRef(0);
    const dragStartWidth = useRef(0);

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // The slash commands are the offered tools': read the kits once the panel opens, if no turn has yet.
    useEffect(() => {
        if (visible && !getKitManifest()) agentService.ensureKits().catch((err: unknown) => console.warn('[AI] kits not loaded:', err));
    }, [visible]);

    // Focus input when panel becomes visible
    useEffect(() => {
        if (visible) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [visible]);

    // --- Resize drag handling ---
    const handleResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isDragging.current = true;
        dragStartY.current = e.clientY;
        dragStartHeight.current = panelHeight;

        const onMouseMove = (ev: MouseEvent) => {
            if (!isDragging.current) return;
            const delta = dragStartY.current - ev.clientY;
            const maxH = window.innerHeight * MAX_PANEL_HEIGHT_RATIO;
            const newHeight = Math.min(maxH, Math.max(MIN_PANEL_HEIGHT, dragStartHeight.current + delta));
            setPanelHeight(newHeight);
        };

        const onMouseUp = () => {
            isDragging.current = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [panelHeight]);

    // --- Diagonal (corner) resize drag handling ---
    const handleResizeCornerStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isDragging.current = true;
        isDraggingWidth.current = true;
        dragStartY.current = e.clientY;
        dragStartHeight.current = panelHeight;
        dragStartX.current = e.clientX;
        dragStartWidth.current = panelWidth;

        const onMouseMove = (ev: MouseEvent) => {
            const maxH = window.innerHeight * MAX_PANEL_HEIGHT_RATIO;
            const newHeight = Math.min(maxH, Math.max(MIN_PANEL_HEIGHT, dragStartHeight.current + (dragStartY.current - ev.clientY)));
            setPanelHeight(newHeight);

            const maxW = Math.min(styles.MAX_PANEL_WIDTH, window.innerWidth - 16);
            const newWidth = Math.min(maxW, Math.max(styles.MIN_PANEL_WIDTH, dragStartWidth.current + (ev.clientX - dragStartX.current)));
            setPanelWidth(newWidth);
        };

        const onMouseUp = () => {
            isDragging.current = false;
            isDraggingWidth.current = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [panelHeight, panelWidth]);

    // --- Horizontal resize drag handling ---
    const handleResizeWidthStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isDraggingWidth.current = true;
        dragStartX.current = e.clientX;
        dragStartWidth.current = panelWidth;

        const onMouseMove = (ev: MouseEvent) => {
            if (!isDraggingWidth.current) return;
            const delta = ev.clientX - dragStartX.current;
            const maxW = Math.min(styles.MAX_PANEL_WIDTH, window.innerWidth - 16);
            const newWidth = Math.min(maxW, Math.max(styles.MIN_PANEL_WIDTH, dragStartWidth.current + delta));
            setPanelWidth(newWidth);
        };

        const onMouseUp = () => {
            isDraggingWidth.current = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [panelWidth]);

    /** Click a tool status indicator to select the associated object */
    const handleClickObject = useCallback((objectPath: string) => {
        const scene = agentService.getScene();
        if (!scene) return;

        // Only a path or name that names one object selects anything.
        const found = resolveObjectRef(scene, objectPath);
        if ('object' in found) scene.select(found.object);
    }, []);

    // ── Slash command suggestions ────────────────────────────
    const slashMatches = useMemo(() => {
        if (slashFilter === null) return [];
        return matchSlashCommands(slashFilter);
    }, [slashFilter]);

    const showSlashSuggestions = slashFilter !== null && slashMatches.length > 0;

    const selectSlashCommand = useCallback((cmd: SlashCommand) => {
        setInputText(`/${cmd.name} `);
        setSlashFilter(null);
        inputRef.current?.focus();
    }, []);

    // ── Slash command direct execution ────────────────────────
    const handleSlashCommand = useCallback(async (text: string): Promise<boolean> => {
        const parsed = parseSlashInput(text);
        if (!parsed) return false;

        const { command, rawArgs } = parsed;
        // A tool whose code a specialist writes runs as a turn: the gateway writes the code.
        if (getToolDefinition(command.toolName)?.specialist) return false;
        const scene = agentService.getScene();
        if (!scene) {
            setMessages(prev => [...prev, {
                id: nextId(), role: 'assistant', content: 'No scene is loaded.',
            }]);
            return true;
        }

        setInputText('');
        if (inputRef.current) inputRef.current.style.height = 'auto';
        setSlashFilter(null);
        setIsProcessing(true);

        const userMsg: MessageData = { id: nextId(), role: 'user', content: text };
        setMessages(prev => [...prev, userMsg]);

        const assistantId = nextId();
        await ensureGatewayDiscovery();
        // Same chip a turn's tool gets: the tool, and the model answering it.
        const model = agentService.toolModelLabel(command.toolName);
        setMessages(prev => [...prev, {
            id: assistantId,
            role: 'assistant',
            content: '',
            segments: [{ type: 'tool', tool: { name: command.toolName, status: 'running', model } }],
        }]);

        try {
            const ctx: ToolContext = {
                ...agentService.getToolContext(command.toolName),
                // The agent loop's sink lives on AgentService for the duration
                // of a turn; a slash command has no turn, so it patches its
                // own single chip.
                onToolProgress: (progress: string) => setMessages(prev => prev.map(m =>
                    m.id === assistantId
                        ? {
                            ...m,
                            segments: [{
                                type: 'tool',
                                tool: { name: command.toolName, status: 'running', model, progress },
                            }],
                        }
                        : m,
                )),
            };
            const result = await executeSlashCommand(command, rawArgs, ctx);
            const isError = isToolErrorResult(result);
            setMessages(prev => prev.map(m => {
                if (m.id !== assistantId) return m;
                return {
                    ...m,
                    content: result,
                    segments: [
                        { type: 'tool', tool: { name: command.toolName, status: isError ? 'error' : 'done', model } },
                        { type: 'text', text: result },
                    ],
                };
            }));
        } catch (err) {
            // A slash command calls the gateway with no turn around it, so
            // nothing upstream has put its failure into words yet.
            const errorText = describeGatewayError(err);
            setMessages(prev => prev.map(m => {
                if (m.id !== assistantId) return m;
                return {
                    ...m,
                    content: `Error: ${errorText}`,
                    segments: [
                        { type: 'tool', tool: { name: command.toolName, status: 'error', model } },
                        { type: 'text', text: `Error: ${errorText}` },
                    ],
                };
            }));
        } finally {
            setIsProcessing(false);
            // A slash command runs outside sendMessage, which is where the
            // post-turn credits refresh lives — without this a /model or
            // /image spend only shows up after a reload.
            if (isGatewayAvailable()) void refreshCredits(true);
        }
        return true;
    }, []);

    // ── Attachments ───────────────────────────────────────────
    // A 429 is waited out inside gatewayFetch; what reaches here failed.
    const uploadAttachment = (id: string, file: File) => {
        uploadGatewayFile(file, file.name).then(
            doc => setAttachments(prev => prev.map(a => (a.id === id ? { ...a, status: 'ready', doc, file: undefined } : a))),
            (err: unknown) => setAttachments(prev => prev.map(a => (a.id === id
                ? { ...a, status: 'error', error: err instanceof Error ? err.message : String(err) }
                : a))),
        );
    };
    // Uploaded the moment they are added, so a slow upload overlaps typing
    // and a refused file (too big, unsupported) says so before the send.
    const addFiles = useCallback(async (files: File[]) => {
        const { scene: sceneFiles, read } = await splitSceneFiles(files);
        if (sceneFiles.length) {
            const id = nextId();
            const name = sceneFiles.length === 1 ? sceneFiles[0].name : `${sceneFiles.length} files`;
            setAttachments(prev => [...prev, { id, name, status: 'uploading', inScene: true }]);
            agentService.addSceneFiles(sceneFiles).then(
                () => setAttachments(prev => prev.map(a => (a.id === id ? { ...a, status: 'ready' } : a))),
                (err: unknown) => setAttachments(prev => prev.map(a => (a.id === id
                    ? { ...a, status: 'error', error: err instanceof Error ? err.message : String(err) }
                    : a))),
            );
        }
        for (const file of read) {
            const id = nextId();
            const refuse = (error: string) => setAttachments(prev => [...prev, { id, name: file.name, status: 'error', error }]);
            if (!isGatewayAvailable()) {
                refuse('Attachments need the Trace AI gateway — sign in to the catalog.');
                continue;
            }
            if (file.size > MAX_ATTACHMENT_BYTES) {
                refuse(`Over the ${MAX_ATTACHMENT_BYTES / 1048576}MB limit.`);
                continue;
            }
            setAttachments(prev => [...prev, { id, name: file.name, status: 'uploading', file }]);
            uploadAttachment(id, file);
        }
    }, []);

    const removeAttachment = (id: string) => setAttachments(prev => prev.filter(a => a.id !== id));
    const retryAttachment = (a: PendingAttachment) => {
        if (!a.file) return;
        setAttachments(prev => prev.map(p => (p.id === a.id ? { ...p, status: 'uploading', error: undefined } : p)));
        uploadAttachment(a.id, a.file);
    };

    const readyAttachments = attachments.filter(a => a.status === 'ready');
    const uploading = attachments.some(a => a.status === 'uploading');
    // A message sent now would go without the file, and the user would not know.
    const failedAttachment = attachments.find(a => a.status === 'error');

    // ── One assistant turn (a new message, or null to continue a paused one) ──
    const runAssistantTurn = useCallback(async (text: string | null, docs: GatewayDocument[]) => {
        const assistantId = nextId();
        const assistantMsg: MessageData = {
            id: assistantId,
            role: 'assistant',
            content: '',
            segments: [],
        };
        setMessages(prev => [...prev, assistantMsg]);

        let accumulatedText = '';
        const appendText = (chunk: string) => {
            accumulatedText += chunk;
            setMessages(prev => prev.map(m => {
                if (m.id !== assistantId) return m;
                const segments = [...(m.segments ?? [])];
                const last = segments[segments.length - 1];
                if (last?.type === 'text') {
                    segments[segments.length - 1] = { type: 'text', text: last.text + chunk };
                } else {
                    segments.push({ type: 'text', text: chunk });
                }
                return { ...m, content: m.content + chunk, segments };
            }));
        };
        try {
            const reply = await agentService.sendMessage(
                text,
                appendText,
                (toolName, status, input, model, progress) => {
                    setMessages(prev => prev.map(m => {
                        if (m.id !== assistantId) return m;
                        const segments = [...(m.segments ?? [])];

                        const detail = input ? getToolDetail(toolName, input) : undefined;
                        const objectPath = input ? getToolObjectRef(toolName, input) : undefined;

                        // Interim status patches the chip that is already
                        // running rather than adding another one.
                        if (progress !== undefined) {
                            for (let i = segments.length - 1; i >= 0; i--) {
                                const seg = segments[i];
                                if (seg.type === 'tool' && seg.tool.name === toolName && seg.tool.status === 'running') {
                                    segments[i] = { type: 'tool', tool: { ...seg.tool, progress } };
                                    break;
                                }
                            }
                            return { ...m, segments };
                        }

                        if (status === 'running') {
                            segments.push({
                                type: 'tool',
                                tool: { name: toolName, status, detail, objectPath, model },
                            });
                        } else {
                            for (let i = segments.length - 1; i >= 0; i--) {
                                const seg = segments[i];
                                if (seg.type === 'tool' && seg.tool.name === toolName && seg.tool.status === 'running') {
                                    segments[i] = {
                                        type: 'tool',
                                        // Detail can arrive only on completion — a hosted
                                        // web search reports its query when it is done.
                                        tool: { ...seg.tool, status, model, progress: undefined, ...(detail ? { detail } : {}) },
                                    };
                                    break;
                                }
                            }
                        }
                        return { ...m, segments };
                    }));
                },
                docs,
            );
            // A refused continue returns its reason without streaming it.
            if (!accumulatedText && reply) appendText(reply);
        } catch (err) {
            // sendMessage has already put the failure in one sentence (billing,
            // sign-in, an unreachable or silent gateway). It is shown after a
            // partial answer too: text that just stops, with no reason, is
            // what a frozen panel looks like.
            const errorText = err instanceof Error ? err.message : String(err);
            setMessages(prev => prev.map(m => {
                if (m.id !== assistantId) return m;
                const segments = [...(m.segments ?? [])];
                // Mark any still-running tools as error
                for (let i = 0; i < segments.length; i++) {
                    const seg = segments[i];
                    if (seg.type === 'tool' && seg.tool.status === 'running') {
                        segments[i] = { type: 'tool', tool: { ...seg.tool, status: 'error' } };
                    }
                }
                segments.push({ type: 'text', text: `Error: ${errorText}` });
                return { ...m, content: m.content ? `${m.content}\n\nError: ${errorText}` : `Error: ${errorText}`, segments };
            }));
        } finally {
            setIsProcessing(false);
        }
        // Also true after a failed continue, so the user can retry it.
        if (agentService.canContinue()) {
            setMessages(prev => prev.map(m => (m.id === assistantId ? { ...m, paused: true } : m)));
        }

        if (autoplay && accumulatedText.trim()) {
            void speakMessage(assistantId, accumulatedText.trim());
        }
    }, [autoplay]);

    // ── Send (AI or slash command) ────────────────────────────
    const handleSend = useCallback(async () => {
        const typed = inputText.trim();
        const sending = attachments.filter(a => a.status === 'ready' && a.doc);
        const inScene = attachments.filter(a => a.status === 'ready' && a.inScene);
        if ((!typed && sending.length === 0 && inScene.length === 0) || isProcessing) return;
        // Not yet: the message would go without the file the user is waiting on.
        if (attachments.some(a => a.status === 'uploading' || a.status === 'error')) return;
        const text = typed || (sending.length ? 'See the attached file.' : 'I added these files to the scene.');

        // Try slash command first
        if (text.startsWith('/')) {
            const handled = await handleSlashCommand(text);
            if (handled) return;
            // Unknown slash command — fall through to AI
        }

        // Text is deliberately left in the box — the send is deferred until
        // the gap is fixed, not discarded.
        if (!agentService.hasCredentials()) {
            resolveCredentialGap();
            return;
        }

        setInputText('');
        setAttachments([]);
        if (inputRef.current) inputRef.current.style.height = 'auto';
        setSlashFilter(null);
        setIsProcessing(true);

        const userMsg: MessageData = {
            id: nextId(),
            role: 'user',
            content: text,
            ...(sending.length || inScene.length ? { attachments: [...inScene, ...sending].map(a => a.name) } : {}),
        };
        setMessages(prev => [...prev, userMsg]);

        await runAssistantTurn(text, sending.map(a => a.doc!));
    }, [inputText, attachments, isProcessing, handleSlashCommand, resolveCredentialGap, runAssistantTurn]);

    /** Resume a paused turn (iteration limit, content filter or rate limit), in a new reply bubble. */
    const handleContinue = useCallback((pausedId: string) => {
        if (isProcessing) return;
        setMessages(prev => prev.map(m => (m.id === pausedId ? { ...m, paused: false } : m)));
        setIsProcessing(true);
        void runAssistantTurn(null, []);
    }, [isProcessing, runAssistantTurn]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Slash suggestion navigation
        if (showSlashSuggestions) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSlashSelectedIndex(i => Math.min(i + 1, slashMatches.length - 1));
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSlashSelectedIndex(i => Math.max(i - 1, 0));
                return;
            }
            if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
                const selected = slashMatches[slashSelectedIndex];
                if (selected) {
                    e.preventDefault();
                    // If only the command name is typed (no args yet), autocomplete
                    const spaceIdx = inputText.indexOf(' ');
                    if (spaceIdx === -1) {
                        selectSlashCommand(selected);
                        return;
                    }
                    // Otherwise, send the command
                }
            }
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void handleSend();
        }
        if (e.key === 'Escape') {
            if (showSlashSuggestions) {
                setSlashFilter(null);
                return;
            }
            if (isProcessing) {
                agentService.cancel();
                setIsProcessing(false);
                // Mark any still-running tools as error
                setMessages(prev => prev.map(m => {
                    const segments = m.segments;
                    if (!segments?.some(s => s.type === 'tool' && s.tool.status === 'running')) return m;
                    return {
                        ...m,
                        segments: segments.map(seg =>
                            seg.type === 'tool' && seg.tool.status === 'running'
                                ? { type: 'tool' as const, tool: { ...seg.tool, status: 'error' as const } }
                                : seg,
                        ),
                    };
                }));
            } else {
                onClose();
            }
        }
    };

    const handleClear = () => {
        setMessages([]);
        agentService.clearHistory();
    };

    if (!visible) {
        return null;
    }

    // Files dropped on the panel attach to the message. Stopped here so the
    // editor's own drop handler (scene import) never sees them.
    const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes('Files');

    return (
        <div
            style={{
                ...styles.chatContainer,
                height: panelHeight,
                width: panelWidth,
                ...(dropActive ? { outline: `2px dashed ${styles.colors.accent}`, outlineOffset: -2 } : {}),
            }}
            onDragOver={(e) => {
                if (!hasFiles(e)) return;
                e.preventDefault();
                e.stopPropagation();
                setDropActive(true);
            }}
            onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropActive(false);
            }}
            onDrop={(e) => {
                if (!hasFiles(e)) return;
                e.preventDefault();
                e.stopPropagation();
                setDropActive(false);
                void addFiles(Array.from(e.dataTransfer.files));
            }}
        >
            {/* Top-right corner resize handle (diagonal) */}
            <div
                style={styles.resizeHandleCorner}
                onMouseDown={handleResizeCornerStart}
            />

            {/* Right-edge resize handle */}
            <div
                style={styles.resizeHandleRight}
                onMouseDown={handleResizeWidthStart}
            />

            {/* Resize handle */}
            <div
                style={styles.resizeHandle}
                onMouseDown={handleResizeStart}
            >
                <div style={styles.resizeHandleBar} />
            </div>

            {/* Header bar */}
            <div style={styles.panelHeader}>
                {/* Close button — sparkle icon matching header toggle */}
                <button
                    onClick={onClose}
                    style={{
                        ...styles.iconButton,
                        color: styles.colors.accent,
                    }}
                    title="Close (Esc)"
                >
                    <SparkleIcon />
                </button>

                <span style={{
                    fontSize: 12,
                    color: styles.colors.textMuted,
                    fontWeight: 500,
                    userSelect: 'none',
                }}>AI Assistant</span>

                <span style={{
                    fontSize: 10,
                    color: styles.colors.textMuted,
                    opacity: 0.7,
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    userSelect: 'none',
                }}>{modelString}</span>

                <CreditsChip />

                <div style={{ flex: 1 }} />

                {/* Auto-play TTS toggle */}
                <button
                    onClick={toggleAutoplay}
                    style={{
                        ...styles.iconButton,
                        color: autoplay ? styles.colors.accent : styles.colors.textMuted,
                    }}
                    title={autoplay ? 'Auto-play voice: on' : 'Auto-play voice: off'}
                >
                    {autoplay ? (
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 2.5 4.5 5.5H2v5h2.5L8 13.5v-11z" />
                            <path d="M10.5 5.5a3 3 0 0 1 0 5" stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round" />
                            <path d="M12 4a5 5 0 0 1 0 8" stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round" />
                        </svg>
                    ) : (
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 2.5 4.5 5.5H2v5h2.5L8 13.5v-11z" />
                            <path d="M11 6l4 4M15 6l-4 4" stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round" />
                        </svg>
                    )}
                </button>

                {/* Settings gear */}
                <button
                    onClick={() => setShowSettings(!showSettings)}
                    style={{
                        ...styles.iconButton,
                        color: showSettings ? styles.colors.accent : styles.colors.textMuted,
                    }}
                    title="AI settings"
                >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
                        <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.421 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.421-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115l.094-.319z"/>
                    </svg>
                </button>

                {/* Clear conversation */}
                {messages.length > 0 && (
                    <button
                        onClick={handleClear}
                        style={styles.iconButton}
                        title="Clear conversation"
                    >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4L4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                        </svg>
                    </button>
                )}
            </div>

            {showSettings ? (
                /* Settings replaces messages + prompt bar so it can scroll freely */
                <ApiKeySettings
                    onClose={() => setShowSettings(false)}
                />
            ) : (
                <>
                    {/* Messages area */}
                    <div style={styles.messagesContainer}>
                        {messages.length === 0 && !isProcessing && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                                gap: 8,
                                color: styles.colors.textMuted,
                                fontSize: 12,
                                userSelect: 'none',
                            }}>
                                <SparkleIcon size={24} />
                                {gapCopy === null ? (
                                    <span>Ask AI or type / for commands</span>
                                ) : (
                                    /* First thing a blocked creator sees —
                                       the fix, not a description of the problem. */
                                    <>
                                        <span>{gapCopy.message}</span>
                                        <button
                                            onClick={resolveCredentialGap}
                                            style={{
                                                ...styles.smallButton,
                                                padding: '5px 14px',
                                                fontSize: 12,
                                                background: styles.colors.accent,
                                                color: '#fff',
                                                borderRadius: 4,
                                            }}
                                        >
                                            {gapCopy.action}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                        {messages.map(msg => (
                            <ChatMessageComponent
                                key={msg.id}
                                message={msg}
                                onClickObject={handleClickObject}
                                onContinue={msg.paused && !isProcessing && msg === messages.at(-1)
                                    ? () => handleContinue(msg.id)
                                    : undefined}
                            />
                        ))}
                        {isProcessing && !messages.some(m =>
                            m.segments?.some(s => s.type === 'tool' && s.tool.status === 'running'),
                        ) && <ThinkingIndicator />}
                        <div ref={messagesEndRef} />
                    </div>

                    <GenerationControls />
                    {/* Files waiting to go with the next message */}
                    {attachments.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 10px 0', background: styles.colors.bg }}>
                            {attachments.map(a => (
                                <span
                                    key={a.id}
                                    style={{
                                        ...styles.attachmentChip,
                                        ...(a.status === 'error' ? { borderColor: styles.colors.error, color: styles.colors.error } : {}),
                                        ...(a.status === 'uploading' ? { opacity: 0.6 } : {}),
                                    }}
                                    title={a.error ?? (a.doc?.kind === 'pdf' ? `${a.name} — ${a.doc.pageCount} pages` : a.name)}
                                >
                                    {a.status === 'uploading' ? (a.inScene ? `Adding ${a.name} to the scene…` : `Uploading ${a.name}…`) : a.status === 'error' ? `${a.name}: not uploaded` : a.inScene ? `${a.name} (in scene)` : a.name}
                                    {a.status === 'error' && a.file && (
                                        <button
                                            onClick={() => retryAttachment(a)}
                                            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 11, textDecoration: 'underline' }}
                                        >
                                            Retry
                                        </button>
                                    )}
                                    <button
                                        onClick={() => removeAttachment(a.id)}
                                        style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1 }}
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            {failedAttachment && (
                                <div role="alert" style={{ width: '100%', fontSize: 11, color: styles.colors.error }}>
                                    {`${failedAttachment.name} did not upload (${failedAttachment.error ?? 'unknown error'}). ${failedAttachment.file ? 'Retry or remove' : 'Remove'} it to send.`}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Prompt bar (with suggestions) */}
                    <div style={{ ...styles.promptBar, alignItems: 'flex-end', position: 'relative' }}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept={ATTACHMENT_ACCEPT}
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                void addFiles(Array.from(e.target.files ?? []));
                                e.target.value = '';
                            }}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isProcessing}
                            style={{
                                height: 36,
                                width: 36,
                                padding: 0,
                                background: 'transparent',
                                border: `1px solid ${styles.colors.border}`,
                                borderRadius: 6,
                                color: styles.colors.textMuted,
                                cursor: isProcessing ? 'default' : 'pointer',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            title="Attach a PDF, image or text file, or a model to build with (or paste / drop one)"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                            </svg>
                        </button>
                        {showSlashSuggestions && (
                            <SlashSuggestions
                                filter={slashFilter ?? ''}
                                selectedIndex={slashSelectedIndex}
                                onSelect={selectSlashCommand}
                            />
                        )}
                        <textarea
                            ref={inputRef}
                            value={inputText}
                            onChange={(e) => {
                                const val = e.target.value;
                                setInputText(val);

                                // Track slash command filter
                                if (val.startsWith('/')) {
                                    const spaceIdx = val.indexOf(' ');
                                    // Only show suggestions while typing the command name
                                    if (spaceIdx === -1) {
                                        setSlashFilter(val.slice(1));
                                        setSlashSelectedIndex(0);
                                    } else {
                                        setSlashFilter(null);
                                    }
                                } else {
                                    setSlashFilter(null);
                                }

                                // Auto-grow
                                const el = e.target;
                                el.style.height = 'auto';
                                el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
                            }}
                            onKeyDown={handleKeyDown}
                            onPaste={(e) => {
                                const files = Array.from(e.clipboardData.files);
                                if (files.length === 0) return;
                                e.preventDefault();
                                void addFiles(files);
                            }}
                            placeholder={gapCopy?.placeholder ?? 'Ask something or type / for commands...'}
                            rows={1}
                            style={{
                                ...styles.promptInput,
                                lineHeight: '20px',
                                overflow: 'hidden',
                                overflowY: 'auto',
                                scrollbarWidth: 'none',
                            }}
                        />
                        {/* Mic button */}
                        {voice.available && !isProcessing && (
                            <button
                                onClick={() => void handleMicClick()}
                                style={{
                                    height: 36,
                                    width: 36,
                                    padding: 0,
                                    background: voice.state === 'recording'
                                        ? styles.colors.error
                                        : voice.state === 'transcribing'
                                            ? styles.colors.accent
                                            : 'transparent',
                                    border: voice.state === 'idle' ? `1px solid ${styles.colors.border}` : 'none',
                                    borderRadius: 6,
                                    color: voice.state === 'idle' ? styles.colors.textMuted : styles.colors.textBright,
                                    cursor: voice.state === 'transcribing' ? 'wait' : 'pointer',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.15s ease',
                                }}
                                title={voice.state === 'recording'
                                    ? 'Stop recording'
                                    : voice.state === 'transcribing'
                                        ? 'Transcribing...'
                                        : 'Voice input'}
                                disabled={voice.state === 'transcribing'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z"/>
                                    <path d="M19 11a1 1 0 0 0-2 0 5 5 0 0 1-10 0 1 1 0 0 0-2 0 7 7 0 0 0 6 6.93V21h-3a1 1 0 0 0 0 2h8a1 1 0 0 0 0-2h-3v-3.07A7 7 0 0 0 19 11z"/>
                                </svg>
                            </button>
                        )}
                        {isProcessing ? (
                            <button
                                onClick={() => {
                                    agentService.cancel();
                                    setIsProcessing(false);
                                }}
                                style={styles.stopButton}
                                title="Stop (Esc)"
                            >
                                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                                    <rect width="10" height="10" rx="1" />
                                </svg>
                            </button>
                        ) : (
                            <button
                                onClick={() => void handleSend()}
                                disabled={uploading || !!failedAttachment || (!inputText.trim() && readyAttachments.length === 0)}
                                style={!uploading && !failedAttachment && (inputText.trim() || readyAttachments.length > 0)
                                    ? styles.sendButton
                                    : styles.sendButtonDisabled}
                                title={uploading ? 'Waiting for uploads…' : failedAttachment ? `A file did not upload: ${failedAttachment.file ? 'retry or remove' : 'remove'} it` : 'Send (Enter)'}
                            >
                                Send
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
